import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import type { MessageKey } from 'types/LocaleMessageKey';

import SectionHeading from '@/app/components/SectionHeading';
import TwoColumnModule, {
  type ImageColProps,
  type TextColProps,
} from '@/app/components/TwoColumnModule';

import defaultParagraphs from './_data/defaultParagraphs';

export interface MembershipSectionProps {
  readonly copy: MessageKey;
  readonly heading: MessageKey;
  readonly image: Omit<ImageColProps, 'motionProps'>;
  readonly paragraphs?: TextColProps['paragraphs'];
}

const MembershipSection = ({
  copy,
  heading,
  image,
  paragraphs = defaultParagraphs,
}: MembershipSectionProps) => {
  return (
    <section className="py-10 lg:py-20">
      <LayoutGrid className="mb-10 lg:mb-24">
        <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
          <SectionHeading
            animated
            content={heading}
            typographyProps={{
              className: 'leading-tighter tracking-tighter md:text-center',
              Element: 'h2',
              variant: 'h2',
              weight: 'light',
            }}
          />
        </GridItem>
      </LayoutGrid>
      <div className="xxl:px-[4.5%]">
        <TwoColumnModule
          imageCol={{
            ...image,
            motionProps: {
              initial: { filter: 'blur(5px)', opacity: 0 },
              style: { height: '100%' },
              transition: { delay: 0.5, duration: 0.5 },
              viewport: { once: true },
              whileInView: { filter: 'blur(0px)', opacity: 1 },
            },
          }}
          layoutGridClassName="gap-y-6 lg:gap-y-12"
          textCol={{
            iconSize: 24,
            iconTextContainerClassName: 'flex gap-x-2 lg:gap-x-4',
            paragraphs: paragraphs.map((paragraph) => ({
              ...paragraph,
              iconClassName:
                'bg-sandstone-400 shrink-0 grow-0 flex justify-center items-center size-12 rounded-full text-sandstone-500 lg:size-14',
              textProps: {
                ...paragraph.textProps,
                className:
                  'text-sm flex w-full h-full justify-center items-center lg:text-base',
              },
            })),
            textColClassName: 'grid grid-cols-2 gap-6 pt-4 md:pt-2',
            titleProps: {
              label: copy,
              className: 'mb-6 lg:mb-8 text-base',
              Element: 'h3',
              height: 'tight',
              variant: 'body-large',
            },
          }}
          mdBreakTextPos="right"
        />
      </div>
    </section>
  );
};

export default MembershipSection;
